import React from 'react'
import { GetServerSideProps } from 'next'
import useSWR from 'swr'

import { Layout } from 'components/layout'
import { ArticlesBlock } from '@components/articles-block'
import { HeadMetadata } from 'components/head-metadata'
import { TagsCloud } from '@components/tags-cloud'
import { Pagination } from '@components/shared/pagination'

import { TagProps } from 'services/api/tags-pages'
import { MenusApi, Menu } from '@services/api/menus'

import { Tag, TagIndexPagination } from 'types/tag'

import { convertObjectQueryParams } from '@utils/convert-object-query-params'
import { fetcherDataApi } from '@utils/fetcher-data-api'
import { AdsApi } from '@services/api/ads'
import { AdsIndexDto } from '@services/api/ads/types'
import { SponsorBanner } from '@components/sponsor-banner'

import styles from './styles.module.scss'
import { Divider } from '@components/divider'

interface Props {
  articles: any[]
  data: TagIndexPagination
  url: string
  name: string
  metaTitle: string
  metaDescription: string
  slug: string
  tags: any[]
  main: Menu[]
  footer1: Menu[]
  footer2: Menu[]
  tagData: Tag
  ads: AdsIndexDto[]
}

const TagPage = ({
  data: initialData,
  url,
  tags,
  main,
  footer1,
  footer2,
  tagData,
  ads
}: Props) => {
  const { data } = useSWR(url, fetcherDataApi, {
    initialData
  })

  const { articles, page, pageSize, total } = data

  return (
    <Layout
      sponsorKeyword="tag"
      mainMenus={main}
      footerMenus1={footer1}
      footerMenus2={footer2}
      logoUrl="/noticias"
      ad={ads.find(ad => ad.code === 'ad_sponsor_top')}
    >
      <HeadMetadata
        title={tagData.name}
        metaTitle={tagData.metaTitle}
        metaDescription={tagData.metaDescription}
        canonicalUrl={tagData.canonicalUrl}
      />
      <div className={styles.header}>
        <h1 className={styles.title}>{tagData.name}</h1>
      </div>

      <div className="mb-6">
        <ArticlesBlock
          articles={articles.slice(0, 3)}
          className="grid-cols-3"
        />
      </div>

      <SponsorBanner
        config={ads.find(ad => ad.code === 'ad_sponsor_middle')}
        className="my-10"
      />

      <div className="my-10">
        <Divider type="primary" orientation="horizontal" />
      </div>

      <ArticlesBlock
        articles={articles.slice(3, articles.length + 1)}
        className="grid-cols-4"
      />

      <Pagination currentPage={page} pageSize={pageSize} total={total} />

      <SponsorBanner
        config={ads.find(ad => ad.code === 'ad_sponsor_bottom')}
        className="my-10"
      />

      <TagsCloud tags={tags} className="my-10" />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  try {
    const page = Number(query.page || 1)
    const paramsPagination = {
      page,
      per: 19
    }

    const { error, ...props } = await TagProps(query.tag, paramsPagination)
    const menus = await MenusApi.all()

    const {
      data: { ads }
    } = await AdsApi.index({
      codes: ['ad_sponsor_top', 'ad_sponsor_middle', 'ad_sponsor_bottom']
    })

    if (error?.status === 404) {
      return {
        notFound: true
      }
    }

    const url = convertObjectQueryParams({
      path: `/tags/${query.tag}/articles`,
      object: paramsPagination
    })

    return {
      props: {
        ...props,
        ...menus,
        url,
        ads: ads || null,
        key: Number(new Date())
      }
    }
  } catch (error) {
    return {
      notFound: true
    }
  }
}

export default TagPage
